import React from 'react';
import PropTypes from 'prop-types';
import SearchFilter from '../SearchFilter';
import StateProvider from '../StateProvider';
import { objToQueryString, searchToSearchObj } from '../../utils/url';
import { useTranslation } from 'next-i18next';
import styles from './Search.module.scss';

const toggleItem = (items, item) =>
    items.includes(item) ? items.filter((i) => i !== item) : [...items, item];

const handleHistoryState = (state) => {
    const params = {
        query: state.searchInput,
        pinned: state.searchSettings.pinnedCategory || '',
        categories: state.activeChips.join(','),
        regions: state.activeRegions.join(','),
        sorting: state.sorting,
        view: state.view,
    };

    window.history.replaceState(
        params,
        'search',
        '?' + objToQueryString(params)
    );
};

const reducer = (state, action) => {
    let newState;
    switch (action.type) {
        case 'set_categories':
            return { ...state, categories: action.payload };
        case 'set_sorting':
            newState = { ...state, sorting: action.payload };
            handleHistoryState(newState);
            return newState;
        case 'set_searching':
            return { ...state, searching: action.payload };
        case 'set_meta':
            return { ...state, meta: action.payload };
        case 'set_view':
            newState = { ...state, view: action.payload };
            handleHistoryState(newState);
            return newState;
        case 'set_current_page':
            return { ...state, currentPage: action.payload };
        case 'set_search_input':
            newState = { ...state, searchInput: action.payload };
            handleHistoryState(newState);
            return newState;
        case 'clear_chips':
            return { ...state, activeChips: [] };
        case 'reset_search_input':
            return { ...state, searchInput: '' };
        case 'toggle_active_region':
            newState = {
                ...state,
                activeRegions: action.payload,
            };
            handleHistoryState(newState);
            return newState;
        case 'toggle_active_chip':
            newState = {
                ...state,
                activeChips: toggleItem(state.activeChips, action.payload),
            };
            handleHistoryState(newState);
            return newState;
        default:
            return state;
    }
};

const getBaseState = ({ t }) => {
    return {
        searchInput: '',
        searching: false,
        meta: {},
        currentPage: 1,
        activeChips: [],
        activeRegions: [],
        sorting: 'relevance',
        view: 'grid',
        sortChoices: [
            {
                title: t('search.sortChoices.relevence'),
                path: 'relevance',
            },
            {
                title: t('search.sortChoices.new'),
                path: '-published_at',
            },
            {
                title: t('search.sortChoices.popularity'),
                path: '-popularity',
            },
        ],
    };
};

const Search = ({
    isGlobal = false,
    searchSettings = {
        categories: [],
    },
    children,
    embeddedByContainer,
}) => {
    const { t } = useTranslation('common');

    let searchParams = {};
    if (typeof window !== 'undefined') {
        searchParams = searchToSearchObj(window.location.search);
    }

    if (searchSettings.pinnedCategory) {
        searchParams.pinnedCategory = searchSettings.pinnedCategory;
    } else {
        searchParams.pinnedCategory = '';
    }

    const initialState = {
        ...getBaseState({ t }),
        searchInput: searchSettings.searchInput || '',
        categories: searchSettings.categories,
        regions: searchSettings.regions,
        activeRegions: searchSettings.activeRegions || [],
        activeChips: searchSettings.activeChips || [],
        view: searchParams.view,
        isGlobal: !!isGlobal,
        searchSettings,
        ...searchParams,
    };

    return (
        <StateProvider reducer={reducer} initialState={initialState}>
            <SearchFilter
                untranslatedButtonModifiers={[
                    `SearchFilter--${embeddedByContainer}`,
                ]}>
                {children}
            </SearchFilter>
        </StateProvider>
    );
};

Search.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]),
    destinations: PropTypes.array,
    searchSettings: PropTypes.object,
    isGlobal: PropTypes.bool,
    embeddedByContainer: PropTypes.string,
};

export default Search;
